const CHECK_IN_REDIRECT_PREFIX = 'check-in-auto-redirect-';
const ACTIVITY_COUNT_AT_LAST_REDIRECT_KEY = `${CHECK_IN_REDIRECT_PREFIX}last-activity-count`;

// Sentinel for "no milestone has been auto-opened yet", so the first one always can be.
const NO_PREVIOUS_REDIRECT = -1;

const claimsInMemory = new Map<string, string>();

function readClaim(key: string): string | null {
  const inMemory = claimsInMemory.get(key);
  if (inMemory !== undefined) return inMemory;

  try {
    return sessionStorage.getItem(key);
  } catch {
    // Storage can be blocked; the in-memory claims still prevent redirect loops.
    return null;
  }
}

function addClaim(key: string, value = '1'): void {
  claimsInMemory.set(key, value);

  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Nothing persisted if storage is unavailable.
  }
}

function hasClaim(key: string): boolean {
  return readClaim(key) !== null;
}

/** Treats a missing or tampered value as "never redirected", rather than blocking forever. */
function readActivityCountAtLastRedirect(): number {
  const stored = readClaim(ACTIVITY_COUNT_AT_LAST_REDIRECT_KEY);
  const parsed = Number(stored);
  return stored !== null && Number.isFinite(parsed) ? parsed : NO_PREVIOUS_REDIRECT;
}

/**
 * Claim the single automatic redirect allowed for a milestone.
 *
 * A milestone becomes due either because the user completed more activities, or merely
 * because recording the previous check-in moved the queue up — and only the first
 * deserves an interruption. Requiring the activity count to have grown since the last
 * auto-redirect tells those apart, so a user is never handed a second survey straight
 * after finishing one, yet every milestone can still open in the same tab.
 */
export function markCheckInRedirected(
  checkInNumber: number,
  totalActivitiesCompleted: number
): boolean {
  const key = `${CHECK_IN_REDIRECT_PREFIX}${checkInNumber}`;
  if (hasClaim(key)) return false;
  if (totalActivitiesCompleted <= readActivityCountAtLastRedirect()) return false;

  addClaim(key);
  addClaim(ACTIVITY_COUNT_AT_LAST_REDIRECT_KEY, String(totalActivitiesCompleted));
  return true;
}

/** Allow pending check-ins to auto-redirect again after a user signs back in. */
export function clearCheckInRedirectGuards(): void {
  claimsInMemory.clear();

  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(CHECK_IN_REDIRECT_PREFIX)) sessionStorage.removeItem(key);
    }
  } catch {
    // Nothing persisted if storage is unavailable.
  }
}
